import {
  baseAcademyUrl,
  baseIndexxShopUrl,
  baseLottoUrl,
  baseIndexxWalletUrl,
  baseNewCEXURL,
  basePayUrl,
  decodeJWT,
  getUserShortToken,
} from "../services/api";

/**
 * Where a signed-in user goes instead of a login/sign-up page, and how to send
 * them there without ever leaving the page stuck.
 *
 * Sites ask for a login with ?redirectWebsiteLink=<name or URL>. Names are
 * mapped to their site; full URLs are only followed to indexx.ai sites, so the
 * login page can't be used to bounce someone to a look-alike domain.
 */
const NAMED_SITES: Record<string, () => string> = {
  exchange: () => baseNewCEXURL,
  lotto: () => baseLottoUrl,
  academy: () => baseAcademyUrl,
  shop: () => baseIndexxShopUrl,
  indexxpay: () => basePayUrl,
  indexxwallet: () => baseIndexxWalletUrl,
};

const AUTH_KEYS = ["access_token", "refresh_token", "email", "user", "userType", "username", "shortToken", "userlogged"];

export function resolveRedirectLink(param?: string | null): string | null {
  const value = String(param || "").trim();
  if (!value) return null;
  const named = NAMED_SITES[value.toLowerCase()];
  if (named) return named() || null;
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const trusted = host === "indexx.ai" || host.endsWith(".indexx.ai") || host === "localhost";
    return trusted && (url.protocol === "https:" || host === "localhost") ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * True when this browser holds a sign-in that hasn't expired. An expired one
 * is cleared, so the person can simply log in again instead of being bounced.
 */
export function hasActiveSession(): boolean {
  const token = localStorage.getItem("access_token");
  if (!token || token === "undefined" || token === "null") return false;
  try {
    const decoded: any = decodeJWT(token);
    if (decoded?.exp && decoded.exp * 1000 <= Date.now()) {
      AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
      return false;
    }
    return true;
  } catch {
    AUTH_KEYS.forEach((k) => localStorage.removeItem(k));
    return false;
  }
}

/** A short sign-in token for the target site; never throws, gives up after 5 s. */
async function shortTokenFor(): Promise<string | null> {
  try {
    let email = localStorage.getItem("email");
    if (!email) {
      const decoded: any = decodeJWT(localStorage.getItem("access_token") || "");
      email = decoded?.email || null;
    }
    if (!email) return null;
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000));
    const res: any = await Promise.race([getUserShortToken(email).catch(() => null), timeout]);
    return typeof res?.data === "string" ? res.data : null;
  } catch {
    return null;
  }
}

/** Send a signed-in user on: to the site that asked for the login, else home. */
export async function redirectSignedInUser(redirectParam?: string | null) {
  const target = resolveRedirectLink(redirectParam);
  if (!target) {
    window.location.replace("/");
    return;
  }
  const url = new URL(target);
  const token = await shortTokenFor();
  if (token) url.searchParams.set("signInToken", token);
  window.location.replace(url.toString());
}
